import React, { useEffect, useState, useMemo } from "react";
import BodyWrapper from "../../../components/Wrapper/BodyWrapper";
import Header from "../../../components/Header/Header";
import CustomBtn from "../../../components/Buttons/CustomBtn";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { ExpenseColumns } from "../../../assets/Columns/ExpenseColumns";
import { fetchExpenses } from "../../../store/Slices/ExpenseSlice";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import { useDispatch, useSelector } from "react-redux";
import AddExpenseModal from "../../../components/Modals/AddExpenseModal";
import EditExpenseModal from "../../../components/Modals/EditExpenseModal";
import DeleteModal from "../../../components/Modals/DeleteModal";
import { DeleteExpenseApi } from "../../../ApiRequests";
import { ErrorToast, SuccessToast } from "../../../utils/ShowToast";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ExpenseTypeSelector from "../../../components/Selector/ExpenseTypeSelector";
import AddExpenseTypeModal from "../../../components/Modals/AddExpenseTypeModal";
import { getExpenseTypeName, getExpenseTypeId } from "../../../utils/ExpenseConverter";
import { fetchExpenseTypes } from "../../../store/Slices/ExpenseTypeSlice";
import { toLocalYmd } from "../../../utils/dateTime";

const todayDateStr = () => toLocalYmd();

const Expense = () => {
  const [SearchText, setSearchText] = useState("");
  const [FromDate, setFromDate] = useState(todayDateStr);
  const [ToDate, setToDate] = useState(todayDateStr);

  const [OpenModal, setOpenModal] = useState(false);
  const [OpenEditModal, setOpenEditModal] = useState(false);
  const [OpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [Selected, setSelected] = useState("");
  const [Loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [OpenTypeModal, setOpenTypeModal] = useState(false);

  const dispatch = useDispatch();
  const Expenses = useSelector((state) => state.Expenses);

  useEffect(() => {
    dispatch(fetchExpenses());
    dispatch(fetchExpenseTypes());
  }, [dispatch]);

  const filteredByDateAndType = useMemo(() => {
    return (Expenses.data || []).filter((dt) => {
      const expenseDate = toLocalYmd(dt.date);
      const matchFrom = !FromDate || new Date(expenseDate) >= new Date(FromDate);
      const matchTo = !ToDate || new Date(expenseDate) <= new Date(ToDate);
      const matchType =
        !selectedType?._id ||
        String(getExpenseTypeId(dt)) === String(selectedType._id) ||
        getExpenseTypeName(dt) === selectedType.name;
      return matchFrom && matchTo && matchType;
    });
  }, [Expenses.data, FromDate, ToDate, selectedType]);

  const filteredData = useMemo(
    () => filterTableRowsBySearch(filteredByDateAndType, SearchText, ExpenseColumns),
    [filteredByDateAndType, SearchText]
  );

  // All Expenses Total
  const grandTotal = useMemo(() => {
    return Expenses.data.reduce((sum, item) => sum + (item.expense || 0), 0);
  }, [Expenses.data]);

  // Filtered Total
  const filteredTotal = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (item.expense || 0), 0);
  }, [filteredData]);

  // Grouped Totals by Expense Type
  const groupedTotals = useMemo(() => {
    const groups = {};
    Expenses.data.forEach((item) => {
      const type = getExpenseTypeName(item) || "Other";
      groups[type] = (groups[type] || 0) + (item.expense || 0);
    });
    return groups;
  }, [Expenses.data]);

  return (
    <BodyWrapper>
      <Header title="Expense" desc="Track and manage your expenses effectively">
        <div className="flex gap-2 flex-wrap">
          <CustomBtn
            title={"Add Expense Type"}
            onClick={() => setOpenTypeModal(true)}
          />
          <CustomBtn
            title={"Add New Expense"}
            onClick={() => setOpenModal(true)}
          />
        </div>
      </Header>

      {/* Date filters */}
      <div className="flex justify-between items-end">
        <div className="flex gap-4 px-5 mt-5 mb-2 max-md:flex-col max-md:items-start">
          <div>
            <label className="block text-sm font-medium mb-1">From Date</label>
            <input
              type="date"
              className="border px-3 py-2 rounded w-[180px]"
              value={FromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">To Date</label>
            <input
              type="date"
              className="border px-3 py-2 rounded w-[180px]"
              value={ToDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-1">
          <ExpenseTypeSelector
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            allowClear
          />
        </div>
      </div>

      {/* Table or loader */}
      <div className="w-full mt-4 flex justify-center items-center">
        {Expenses.loading ? (
          <div className="flex flex-1 justify-center items-center">
            <FetchingLoading />
          </div>
        ) : (
          <SearchableTable
            setOpenEditModal={setOpenEditModal}
            setOpenDeleteModal={setOpenDeleteModal}
            setSelected={setSelected}
            SearchPlaceholder={"Search Expense..."}
            SearchText={SearchText}
            setSearchText={setSearchText}
            CurrentData={filteredByDateAndType}
            Columns={ExpenseColumns}
          />
        )}
      </div>

      {/* Summary Totals */}
      {!Expenses.loading && (
        <div className="w-full px-5 mt-6 space-y-2">
          <div className="text-lg font-semibold text-right text-ink">
            Grand Total (All Expenses):{" "}
            <span className="text-main">Rs. {grandTotal}</span>
          </div>

          <div className="text-lg font-semibold text-right text-ink">
            Filtered Total:{" "}
            <span className="text-success">Rs. {filteredTotal}</span>
          </div>

          <div className="mt-4">
            <h2 className="text-md font-bold text-ink underline mb-1">Total by Type:</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {Object.entries(groupedTotals).map(([type, total]) => (
                <div
                  key={type}
                  className="p-2 rounded-lg bg-subtle text-sm flex justify-between border border-line"
                >
                  <span className="font-medium text-inkSecondary">{type}</span>
                  <span className="text-right font-semibold text-main">
                    Rs. {total}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {OpenTypeModal && (
        <AddExpenseTypeModal Open={OpenTypeModal} setOpen={setOpenTypeModal} />
      )}
      {OpenModal && <AddExpenseModal Open={OpenModal} setOpen={setOpenModal} />}
      {OpenEditModal && (
        <EditExpenseModal
          open={OpenEditModal}
          setOpen={setOpenEditModal}
          selectedExpense={Selected}
        />
      )}
      {OpenDeleteModal && (
        <DeleteModal
          Open={OpenDeleteModal}
          setOpen={setOpenDeleteModal}
          Loading={Loading}
          onSubmit={async () => {
            setLoading(true);
            try {
              const response = await DeleteExpenseApi(Selected._id);
              if (response.data.success) {
                setOpenDeleteModal(false);
                SuccessToast("Expense deleted successfully");
                dispatch(fetchExpenses());
              } else {
                ErrorToast(response.data.error?.msg);
              }
            } catch (err) {
              console.error(err);
              ErrorToast(err.response.data.error.msg);
              ErrorToast("Failed to delete Expense!");
            }
            setLoading(false);
          }}
        />
      )}
    </BodyWrapper>
  );
};

export default Expense;
